import React, { useState } from "react";
import { ClientApiProps } from "../../common";
import { ISettings, ISettingsBase, SettingsBase, getApiClient } from "../../api";
import { AlertProvider, Button, Header, Label, TextInput, useValidatedState } from "acma-spa";
import * as yup from "yup";
import { SettingsStore } from "../../stores/SettingsStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface SettingsDetailsProps extends ClientApiProps {
  settings: ISettings
  onSettings: (settings: ISettings) => any
  onSpinner: (text: string) => any
}

const schema: yup.ObjectSchema<ISettingsBase> = yup.object({
  clientId: yup.string(),
  requestMailbox: yup.string().email().label('Request mailbox'),
  alphaTagMailbox: yup.string().email().label('Alpha tag mailbox'),
  authorisationMailbox: yup.string().email().label('Confirmation mailbox'),
  firewallMailbox: yup.string().email().label('Firewall mailbox'),
  alphaTagWebhook: yup.string().label("Alpha tag webhook"),
  authorisationWebhook: yup.string().label("Authorisation web hook")
});

export const SettingsDetails: React.FC<SettingsDetailsProps> = (props) => {

  const [model, setModel, errors, validate] = useValidatedState<ISettingsBase>(props.settings, schema);
  const [status, setStatus] = useState(0);

  const onSave = async () => {
    var res = await validate('#settingsContainer');
    if (!res) {
      return;
    }

    setStatus(0);
    props.onSpinner("Saving...");
    try {
      const [client, abort] = getApiClient(
        props.baseUrl,
        props.b2cDomainName,
        props.msalClient
      );
      const store = new SettingsStore(client);
      var data = await store.updateSettings([new SettingsBase(model)]);
      if (data.length > 0) {
        props.onSettings(data[0]);
      }
      setStatus(1);
    }
    catch {
      setStatus(-1);
    }
    finally {
      props.onSpinner("");
    }
  }

  return <div id="settingsContainer">
    {status != 0 ? <AlertProvider key={status} messages={status >= 0 ? ["Changes saved successfully"] : ["Unexpected error"]} dismiss={true} noHeading={true} color={status >= 0 ? "success" : "danger"} /> : <></>}
    {props.settings.isRepresentative ? <>
      <Header label="Company settings" className="mb-3" />
      <Label label="Email address to receive confirmation requests" id="requestMailbox" className="mb-3" error={errors.requestMailbox}>
        <TextInput value={model.requestMailbox ?? ''} onChange={v => setModel({ requestMailbox: v })} size="xl" maxLength={400} />
        {!model.requestMailbox ?
          <FontAwesomeIcon
            icon={'exclamation-triangle'}
            size={'xl'}
            className="text-danger pl-2 align-middle"
            title="Mailbox to recieve confirmation requests has not been specified."
          /> : <></>}
      </Label>
    </> : <></>}

    {props.settings.isMno ? <>
      <Header label="MNO settings" className="mb-3" />
      <Label label="Email address to receive notifications for new/removed alpha tags that are subject to firewall rules" id="firewallMailbox" className="mb-3" error={errors.firewallMailbox}>
        <TextInput value={model.firewallMailbox ?? ''} onChange={v => setModel({ firewallMailbox: v })} size="xl" maxLength={400} />
      </Label>
    </> : <></>}

    {props.settings.isParticipant ? <>
      <Header label="SMS sender settings" className="mb-3" />
      <Label label="Email address to receive notifications for new/removed alpha tags" id="alphaTagMailbox" className="mb-3" error={errors.alphaTagMailbox}>
        <TextInput value={model.alphaTagMailbox ?? ''} onChange={v => setModel({ alphaTagMailbox: v })} size="xl" maxLength={400} />
      </Label>
      <Label label="Email address to receive confirmation notifications" id="authorisationMailbox" className="mb-3" error={errors.authorisationMailbox}>
        <TextInput value={model.authorisationMailbox ?? ''} onChange={v => setModel({ authorisationMailbox: v })} size="xl" maxLength={400} />
      </Label>
      <Label label="URL to receive notifications when a new alpha tag is registered or deregistered" id="alphaTagWebhook" className="mb-3" error={errors.alphaTagWebhook}>
        <TextInput value={model.alphaTagWebhook ?? ''} onChange={v => setModel({ alphaTagWebhook: v })} size="xl" maxLength={400} />
      </Label>
      <Label label="URL to receive notifications when confirmation is issued or revoked for that or another alpha tag" id="authorisationWebhook" className="mb-3" error={errors.authorisationWebhook}>
        <TextInput value={model.authorisationWebhook ?? ''} onChange={v => setModel({ authorisationWebhook: v })} size="xl" maxLength={400} />
      </Label>
      {props.apiPortal ? <>
        <Header label="System to system integration" className="mb-3" />
        <p>The Sender ID registry supports system to system integration via Web APIs.</p>
        <Button id="developer" label="Access API portal" onClick={() => window.open(props.apiPortal)} mode="secondary" />
      </> : <></>}
    </> : <></>}
    <hr />
    <Button id="save" label="Save changes" onClick={onSave} />
  </div>
}